/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from '@app/services/common.service';
import { SelectValue } from '@app/shared/search-select/search-select.component';
import { LoginResp } from '@app/types/auth.type';
import {
  Business_Context,
  Company,
  Entity,
  Gstin,
  IGstr1StatusList,
  User,
} from '@app/types/global.type';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root',
})
export class Store {
  router = inject(Router);
  ngbModal = inject(NgbModal);
  commonSvc = inject(CommonService);

  appLoader: WritableSignal<boolean> = signal<boolean>(false);
  roundLoader: WritableSignal<boolean> = signal<boolean>(false);
  authToken: WritableSignal<string | null> = signal<string | null>(null);
  user: WritableSignal<User | null> = signal<User | null>(null);

  miniSidenav: WritableSignal<boolean> = signal<boolean>(false);
  sideNavHide: WritableSignal<boolean> = signal<boolean>(false);

  businessContext: WritableSignal<Business_Context | null> =
    signal<Business_Context | null>(null);

  filingPeriod: WritableSignal<SelectValue | null> = signal<SelectValue | null>(null);

  financialYear: WritableSignal<SelectValue | null> = signal<SelectValue | null>(null);

  isIframe: WritableSignal<boolean> = signal<boolean>(false);

  businessEntityList: WritableSignal<Entity[]> = signal<Entity[]>([]);

  gstr1StatusList: WritableSignal<IGstr1StatusList[]> = signal<IGstr1StatusList[]>([]);

  selectedEntity: WritableSignal<Entity | null> = signal<Entity | null>(null);

  gstinList: WritableSignal<Gstin[] | []> = signal<Gstin[] | []>([]);

  tenant: WritableSignal<string> = signal<string>('asp');

  company: WritableSignal<Company | null> = signal<Company | null>(null);

  selectedPan: WritableSignal<string> = signal<string>('');

  selectedGstin: WritableSignal<string> = signal<string>('');

  constructor() {
    //sync user token
    const authToken = localStorage.getItem('authToken') ?? '';
    this.authToken.set(authToken);

    //sync user
    const user = JSON.parse(localStorage.getItem('user') ?? 'null');
    this.user.set(user);

    const miniSidenav = localStorage.getItem('miniSidebar');
    this.miniSidenav.set(miniSidenav === 'true');

    const businessContext = JSON.parse(localStorage.getItem('businessContext') ?? 'null');
    this.businessContext.set(businessContext);

    const filingPeriod = localStorage.getItem('filingPeriod');
    this.filingPeriod.set(filingPeriod);

    const pan = localStorage.getItem('selectedPan');
    this.selectedPan.set(pan ?? '');

    const gstin = localStorage.getItem('selectedGstin');
    this.selectedGstin.set(gstin ?? '');

    const financialYear = localStorage.getItem('financialYear');
    this.financialYear.set(financialYear);

    const businessEntityList = JSON.parse(
      localStorage.getItem('businessEntityList') ?? '[]',
    );
    this.businessEntityList.set(businessEntityList);

    const selectedEntity = JSON.parse(localStorage.getItem('selectedEntity') ?? 'null');
    this.selectedEntity.set(selectedEntity);

    const gstinList = JSON.parse(localStorage.getItem('gstinList') ?? '[]');
    this.gstinList.set(gstinList);

    const iFrameVal = localStorage.getItem('iFrameVal');
    this.isIframe.set(iFrameVal === 'true');

    const tenant = localStorage.getItem('sapphireTenant') ?? 'asp';
    this.tenant.set(tenant);

    const company = JSON.parse(localStorage.getItem('company') ?? 'null');
    this.company.set(company);
  }

  setIframe(value: boolean) {
    localStorage.setItem('iFrameVal', JSON.stringify(value));
    this.isIframe.set(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUser(data: LoginResp) {
    const user = {
      email: data.email,
      username: data.username,
      products: data.products,
      companyid: data.companyid,
      rootCompanyName: data.rootCompanyName,
    };

    this.authToken.set(data.token);
    this.user.set(user);

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(user));
  }

  setBusiness(data: Business_Context) {
    const businessContextData = {
      companyid: data?.companyid,
      companyName: data?.companyName,
      rootCompanyName: data?.rootCompanyName,
      rootCompanyId: data?.rootCompanyId,
      gstinNo: data?.gstinNo,
      registrationDate: data?.registrationDate,
    };
    this.businessContext.set(businessContextData);
    localStorage.setItem('businessContext', JSON.stringify(businessContextData));
  }

  setFilingPeriod(value: SelectValue) {
    this.filingPeriod.set(value);
    localStorage.setItem('filingPeriod', value?.toString());
  }

  setPan(value: string) {
    this.selectedPan.set(value);
    localStorage.setItem('selectedPan', value?.toString());
  }

  setGstin(value: string) {
    this.selectedGstin.set(value);
    localStorage.setItem('selectedGstin', value?.toString());
  }

  setFinancialYear(value: SelectValue) {
    this.financialYear.set(value);
    localStorage.setItem('financialYear', value?.toString());
  }

  setBusinessEntityList(list: Entity[]) {
    this.businessEntityList.set(list);
    localStorage.setItem('businessEntityList', JSON.stringify(list));
  }

  setSelectedEntity(value: Entity) {
    this.selectedEntity.set(value);
    localStorage.setItem('selectedEntity', JSON.stringify(value));
  }

  setGstinList(value: Gstin[] = []) {
    this.gstinList.set(value);
    localStorage.setItem('gstinList', JSON.stringify(value));
  }

  setTenant(value: string) {
    const tenant = value?.replace(/"/g, '');
    this.tenant.set(tenant);
    localStorage.setItem('sapphireTenant', tenant);
  }

  logout() {
    this.ngbModal.dismissAll();

    localStorage.clear();
    this.authToken.set(null);

    // TODO: redirect to UUM
    //window.location.reload()
    this.router.navigate(['/login']);
  }

  setCompany(company: Company | null) {
    this.company.set(company);
    localStorage.setItem('company', JSON.stringify(company));
  }
}
